import { FunctionComponent, useEffect, useState } from "react";
import { DeliveryChargeRuleScope, IDeliveryChargeRule } from "./interfaces";
import { Edge, useEdgesState, useNodesState,   Node, ReactFlow, Controls, Background, BackgroundVariant, MarkerType, } from "@xyflow/react";
import { getGovernorateForDistrict, governorates, regions } from "../../../../data/regions";
import { GovernorateNode } from "./custom-nodes/governorate";
import '@xyflow/react/dist/style.css';
import AutoFitView from "../../accouting/cash-flow/auto-fit";
import { highlightZoneFunc } from "./delivery-charge-overrides";
import { DistrictNode } from "./custom-nodes/district";
import { AreaNode } from "./custom-nodes/area";
import { alpha, CircularProgress } from "@mui/material";
import { purple } from "@mui/material/colors";
import { MerchantType } from "../../../../interfaces/delivery-company/merchants";


interface DeliveryRulesGraphProps {
    rules: IDeliveryChargeRule[]
    highlightZone: highlightZoneFunc
    focus: IDeliveryChargeRule | undefined
    setFocus: React.Dispatch<React.SetStateAction<IDeliveryChargeRule | undefined>>
    isLoading: boolean
    editRule: (r: IDeliveryChargeRule) => void;
    merchant: MerchantType
    readonly?:boolean
    canAdd?: boolean
    canEdit?: boolean
    canDelete?: boolean
}

export const nodeTypes = {
  governorate: GovernorateNode,
  district: DistrictNode,
  area: AreaNode,
};
 
const DeliveryRulesGraph: FunctionComponent<DeliveryRulesGraphProps> = ({
        rules,
        highlightZone,
        focus,
        isLoading,
        editRule,
        merchant,
        canAdd,
        canEdit,
        canDelete,
        readonly = false,
    }) => {
    const [nodes, setNodes, onNodesChange] = useNodesState([] as Node[]);
    const [expandedGovernorate, setExpandedGovernorate] = useState<string>()
    const [expandedDistrict, setExpandedDistrict] = useState<string>()

    const center = { x: 600, y: 0 }

    useEffect(()=>{
        console.log("Fired with focus = ", focus)
        if (focus) {
            if (focus.scope == DeliveryChargeRuleScope.DISTRICT) {
                setExpandedDistrict(focus.district)
                setExpandedGovernorate(getGovernorateForDistrict(focus.district!))
            } 
            
            if (focus.scope == DeliveryChargeRuleScope.AREA) {
                setExpandedDistrict(focus.area!.district)
                setExpandedGovernorate(getGovernorateForDistrict(focus.area!.district))
            }
        } else {
            setExpandedGovernorate(undefined)
            setExpandedDistrict(undefined)
        }
    }, [focus])

    console.log("Expanded governorate = ", expandedGovernorate)
    console.log("Expanded district = ", expandedDistrict)

   
    useEffect(() => {
    let nodes: Node[] = [];

    if (!expandedGovernorate) {
        // Case 1: Nothing expanded - show all governorates in two rows
        const half = Math.ceil(governorates.length / 2);
        const row1Governorates = governorates.slice(0, half);
        const row2Governorates = governorates.slice(half);

        const row1Y = center.y - 250;
        const row2Y = center.y;
        const startX = center.x - (Math.max(row1Governorates.length, row2Governorates.length) - 1) * 200;

        // Create nodes for first row
        const row1Nodes = row1Governorates.map((gov, index) => ({
            id: `governorate-${gov}`,
            type: 'governorate',
            position: { x: startX + 400 * index, y: row1Y },
            data: {
                name: gov,
                rules: rules,
                editRule,
                readonly: readonly,
                highlightZone: highlightZone,
                toggleExpand: () => {
                    setExpandedGovernorate(gov);
                    setExpandedDistrict('');
                },
                expanded: false,
                merchant,
                dimmed: false
            },
        }));

        // Create nodes for second row
        const row2Nodes = row2Governorates.map((gov, index) => ({
            id: `governorate-${gov}`,
            type: 'governorate',
            position: { x: startX + 400 * index, y: row2Y },
            data: {
                name: gov,
                rules: rules,
                editRule,
                readonly: readonly,
                highlightZone: highlightZone,
                toggleExpand: () => {
                    setExpandedGovernorate(gov);
                    setExpandedDistrict('');
                },
                expanded: false,
                dimmed: false,
                merchant,
            },
        }));

        nodes = [...row1Nodes, ...row2Nodes];
    } else {
        // Case 2: Governorate is expanded - show only that governorate and its districts
        const governorateNode: Node = {
            id: `governorate-${expandedGovernorate}`,
            type: 'governorate',
            position: { x: center.x, y: center.y - 260 },
            data: {
                name: expandedGovernorate,
                rules: rules,
                editRule,
                readonly: readonly,
                highlightZone: highlightZone,
                toggleExpand: () => {
                    setExpandedGovernorate('');
                    setExpandedDistrict('');
                },
                expanded: true,
                dimmed: false,
                merchant,
            },
        };

        nodes.push(governorateNode);

        const visibleDistricts = regions
            .filter(item => item.governorate === expandedGovernorate)
            .map(item => item.district);

        if (!expandedDistrict) {
            // Case 2a: Only governorate expanded - show districts in a row below
            const districtStartX = center.x - ((visibleDistricts.length - 1) * 150)/2;
            
            const districtNodes = visibleDistricts.map((district, index) => ({
                id: `district-${district}`,
                type: 'district',
                position: { 
                    x: districtStartX + 350 * index,
                    y: center.y
                },
                data: {
                    name: district,
                    rules: rules,
                    editRule,
                    readonly: readonly,
                    highlightZone: highlightZone,
                    expanded: false,
                    toggleExpand: () => {
                        setExpandedDistrict(district);
                    },
                    dimmed: false  ,
                    merchant,
                },
            }));
            nodes.push(...districtNodes);
        } else {
            // Case 3: District is expanded - show governorate, district, and its areas
            const districtNode: Node = {
                id: `district-${expandedDistrict}`,
                type: 'district',
                position: { 
                    x: center.x,
                    y: center.y
                },
                data: {
                    name: expandedDistrict,
                    rules: rules,
                    editRule,
                    readonly: readonly,
                    highlightZone: highlightZone,
                    expanded: true,
                    toggleExpand: () => {
                        setExpandedDistrict('');
                    },
                    dimmed: false  ,
                    merchant,
                },
            };

            nodes.push(districtNode);

            // Add area nodes if district is expanded
            const areaRules = rules.filter(
                rule => rule.scope === DeliveryChargeRuleScope.AREA && 
                       rule.area?.district === expandedDistrict
            );

            // Split areas into chunks of max 6 per row
            const chunkSize = 6;
            const areaRows = [];
            for (let i = 0; i < areaRules.length; i += chunkSize) {
                areaRows.push(areaRules.slice(i, i + chunkSize));
            }

            areaRows.forEach((rowAreas, rowIndex) => {
                const rowStartX = center.x - ((rowAreas.length - 1) * 150) / 2;
                const rowY = center.y + 230 + (rowIndex * 190); // Add vertical spacing for each row
                
                const areaNodes = rowAreas.map((rule, index) => {
                    const area = rule.area!;
                    return {
                        id: `area-${area.code}`,
                        type: 'area',
                        position: { 
                            x: rowStartX + 300 * index,
                            y: rowY
                        },
                        data: {
                            area: area,
                            rules: rules,
                            editRule,
                            readonly: readonly,
                            highlightZone: highlightZone,
                            merchant: merchant
                        },
                    };
                });

                nodes.push(...areaNodes);
            });
        }
    }

    setNodes(nodes);
}, [rules, expandedGovernorate, expandedDistrict]);
    return <>
    <div
        style={{
          height: `calc(100vh - 190px)`,
          boxSizing: 'border-box',
        }}
      >
            {
                isLoading ? <CircularProgress /> : <ReactFlow
                    nodes={nodes}
                    onNodesChange={onNodesChange}
                    nodeTypes={nodeTypes}
                    fitView
                    style={{ backgroundColor: 'transparent',  }}
                    nodesDraggable={false}
                >
                    <AutoFitView
                        dependencies={[
                            nodes,
                        ]}
                         padding={0.3}
                    />
                    
                </ReactFlow>  
            }
          
      </div>
    </>;
}
 
export default DeliveryRulesGraph;